import { EnqueueSnackbar } from "notistack";

import { captureApiErrorInSentry, captureTimeoutErrorInSentry } from "./sentryErrorTracking";

export async function enqueueErrorMessage(response: Response, enqueueSnackbar: EnqueueSnackbar): Promise<void> {
    return handleApiError(response, enqueueSnackbar, {
        shouldShowSnackbar: true,
        isRetry: false,
    });
}

/**
 * Handles API error responses by showing user-friendly messages and tracking in Sentry
 */
export async function handleApiError(
    response: Response,
    enqueueSnackbar: EnqueueSnackbar,
    options?: {
        shouldShowSnackbar?: boolean;
        isRetry?: boolean;
        attempt?: number;
        maxRetries?: number;
    }
): Promise<void> {
    const {
        shouldShowSnackbar = true,
        isRetry = false,
        attempt = 0,
        maxRetries = 0,
    } = options || {};

    // Helper functions to get and set the counter in localStorage
    const getErrorCount = () => parseInt(localStorage.getItem("errorCount") || "0", 10);
    const setErrorCount = (count: number) => localStorage.setItem("errorCount", count.toString());
    const resetErrorCount = () => localStorage.setItem("errorCount", "0");

    // Increment error count
    const currentCount = getErrorCount();
    const errorCount = currentCount + 1;
    setErrorCount(errorCount);

    // Get error data
    const requestId = response.headers.get("x-request-id-resolved");
    const type = "API";
    const { url, status, statusText } = response;

    // Process response body
    let errorMessage = "";
    try {
        const contentType = response.headers.get("content-type");

        if (contentType?.includes("application/json")) {
            const data = await response.clone().json();
            errorMessage = data?.message || JSON.stringify(data, null, 2);
        } else {
            errorMessage = await response.clone().text();
        }
    } catch (err) {
        errorMessage = "Unknown error (failed to parse response)";
    }

    if (errorMessage === "" || status === 500) {
        errorMessage = "No detail available";
    }

    // Capture error in Sentry with full context
    if (isRetry) {
        captureTimeoutErrorInSentry(response, errorMessage, attempt, maxRetries);
    } else {
        captureApiErrorInSentry(response, errorMessage, {
            errorCount,
            localStorageErrorCount: currentCount,
        });
    }

    // Show snackbar to user if requested
    if (shouldShowSnackbar) {
        // Construct the errorString to show on snackbar
        const errorString = `Type: ${type}
URL: ${url}
ErrorStatus: ${status} ${statusText}
ErrorMessage: "${errorMessage}"
Request ID: ${requestId}`;

        // Push snackbar to snackbar queue
        enqueueSnackbar("We're sorry, but an error has occurred. Please try reloading the page", {
            variant: "customError",
            autoHideDuration: 5000,
            errorCount,
            errorString,
        });
    }

    // Reset the error count after 5 errors
    if (errorCount > 4) {
        resetErrorCount();
    }
}

